import myAxios from "../Component/AxiosUrl";

/**
 * Analytics Service
 * Centralized service for all admin analytics API calls
 */

const analyticsService = {
  /**
   * Get dashboard analytics (online users, signups, profile completion)
   */
  getDashboardAnalytics: async () => {
    try {
      const uid = localStorage.getItem("id");
      const response = await myAxios.post(
        `/fetch-dashboard-analytics/${uid}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching dashboard analytics:", error);
      throw error;
    }
  },

  /**
   * Get revenue analytics (revenue, orders, subscriptions)
   * @param {Object} params - Optional date range { dateFrom, dateTo }
   */
  getRevenueAnalytics: async (params = {}) => {
    try {
      const uid = localStorage.getItem("id");
      const response = await myAxios.post(
        `/fetch-revenue-analytics/${uid}`,
        params
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching revenue analytics:", error);
      throw error;
    }
  },

  /**
   * Get user growth analytics (signup trends, deactivations)
   * @param {number} days - Number of days to analyze (default: 30)
   */
  getUserGrowthAnalytics: async (days = 30) => {
    try {
      const uid = localStorage.getItem("id");
      const response = await myAxios.post(
        `/fetch-user-growth-analytics/${uid}`,
        { days }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching user growth analytics:", error);
      throw error;
    }
  },

  /**
   * Get engagement analytics (interests, chats, views)
   */
  getEngagementAnalytics: async () => {
    try {
      const uid = localStorage.getItem("id");
      const response = await myAxios.post(
        `/fetch-engagement-analytics/${uid}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching engagement analytics:", error);
      throw error;
    }
  },

  /**
   * Get SMS analytics (SMS campaigns tracking)
   */
  getSmsAnalytics: async () => {
    try {
      const uid = localStorage.getItem("id");
      const response = await myAxios.post(`/fetch-sms-analytics/${uid}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching SMS analytics:", error);
      throw error;
    }
  },

  /**
   * Get push notification analytics
   */
  getPushAnalytics: async () => {
    try {
      const uid = localStorage.getItem("id");
      const response = await myAxios.post(`/fetch-push-analytics/${uid}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching push analytics:", error);
      throw error;
    }
  },

  /**
   * Get logs with pagination and filters
   * @param {Object} params - Filter parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Records per page
   * @param {string} params.logType - Log type filter (sms, push, admin_action, user_activity, system_event, error)
   * @param {string} params.status - Status filter (success, failed, pending)
   * @param {string} params.userId - Filter by user ID
   * @param {string} params.search - Search term
   * @param {string} params.dateFrom - Start date
   * @param {string} params.dateTo - End date
   * @param {string} params.sortBy - Sort field
   * @param {string} params.sortOrder - Sort order (asc, desc)
   */
  getLogs: async (params = {}) => {
    try {
      const response = await myAxios.post("/fetch-logs", params);
      return response.data;
    } catch (error) {
      console.error("Error fetching logs:", error);
      throw error;
    }
  },

  /**
   * Get event analytics
   * @param {Object} params - Filter parameters
   * @param {string} params.dateFrom - Start date
   * @param {string} params.dateTo - End date
   * @param {string} params.platform - Platform filter (android, ios, web)
   * @param {string} params.source - Source filter (facebook, google, custom, app)
   * @param {string} params.eventName - Event name filter
   * @param {string} params.eventType - Event type filter (standard, custom)
   * @param {number} params.page - Page number
   * @param {number} params.limit - Records per page
   */
  getEventAnalytics: async (params = {}) => {
    try {
      const response = await myAxios.post("/fetch-event-analytics", params);
      return response.data;
    } catch (error) {
      console.error("Error fetching event analytics:", error);
      throw error;
    }
  },

  /**
   * Get attribution analytics (marketing campaigns, UTM tracking, Facebook/Google)
   * @param {Object} params - Filter parameters
   * @param {string} params.dateFrom - Start date
   * @param {string} params.dateTo - End date
   * @param {string} params.campaign - Campaign filter
   * @param {string} params.source - Source filter
   */
  getAttributionAnalytics: async (params = {}) => {
    try {
      const response = await myAxios.post(
        "/fetch-attribution-analytics",
        params
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching attribution analytics:", error);
      throw error;
    }
  },

  /**
   * Get funnel analytics (conversion funnel with drop-off rates)
   * @param {Object} params - Filter parameters
   * @param {string} params.dateFrom - Start date
   * @param {string} params.dateTo - End date
   * @param {array} params.events - Array of event names for funnel steps
   * @param {string} params.platform - Platform filter (android, ios, web)
   * @param {string} params.source - Source filter
   */
  getFunnelAnalytics: async (params = {}) => {
    try {
      const response = await myAxios.post("/fetch-funnel-analytics", params);
      return response.data;
    } catch (error) {
      console.error("Error fetching funnel analytics:", error);
      throw error;
    }
  },
};

export default analyticsService;
